import Highcharts from 'highcharts'

// As of highcharts v9, most attributes are blocked & need to be explicitly allowed
Highcharts.AST.allowedAttributes.push(
    'data-reactroot',
    'onclick', // for tooltip add event btn
    'view-box', // for svg icon, highcharts issue#16229
    'xmlns', // for svg icon, highcharts issue#16229
    'rx',
    'ry',
    'data-z-index',
    'strikewidth'
)

/** Internal legend instance shape used by the wrap (not fully exposed in Highcharts types) */
interface LegendWithInternals {
    options: Highcharts.LegendOptions & {
        doSplit?: boolean
        considerSymbols?: boolean
    }
    legendWidth: number
    itemHeight: number
    allItems: Array<{
        options: { yAxis?: number }
        userOptions?: { side?: string }
        legendGroup?: unknown
        legendItem?: { group: unknown }
        itemWidth: number
    }>
}

/**
 * Wraps the legend rendering function of Highcharts to
 * allow for splitting the legend into two columns.
 */
function wrapLegendRendering(H: typeof Highcharts): void {
    function wrapLegend(
        this: Highcharts.Legend,
        proceed: (...args: unknown[]) => void,
        ...args: unknown[]
    ): void {
        const legend = this as unknown as LegendWithInternals

        // Run the original proceed method
        proceed.apply(this, args)

        const legendOptions = legend.options
        if (!legendOptions.doSplit) {
            return
        }

        const middle = legend.legendWidth / 2
        const whiteSpaceSize = 0.05 * legend.legendWidth

        const rightLegendBegin = middle + whiteSpaceSize
        const leftLegendEnd = middle - whiteSpaceSize

        let currentXLeftLegend = 50
        let currentXRightLegend = legend.legendWidth
        let currentYLeftLegend = 0
        let currentYRightLegend = 0
        const legendLineHeight = legend.itemHeight + 3

        const sortedItems = legend.allItems.toSorted(
            (a, b) => (b.options?.yAxis ?? 0) - (a.options?.yAxis ?? 0)
        )
        sortedItems.forEach((item) => {
            const theGroup = item.legendGroup ?? item.legendItem?.group
            if (!theGroup) {
                return
            }

            const userOptions = item.userOptions ?? {}
            if (userOptions.side === 'right') {
                currentXRightLegend -= item.itemWidth
                if (currentXRightLegend < rightLegendBegin) {
                    currentXRightLegend = legend.legendWidth - item.itemWidth
                    currentYRightLegend += legendLineHeight
                }
                ;(
                    theGroup as {
                        attr: (p: {
                            translateX: number
                            translateY: number
                        }) => void
                    }
                ).attr({
                    translateX: currentXRightLegend,
                    translateY: currentYRightLegend,
                })
                if (legendOptions.considerSymbols) {
                    currentXRightLegend += 10
                }
            } else if (userOptions.side === 'left') {
                if (currentXLeftLegend + item.itemWidth > leftLegendEnd) {
                    currentXLeftLegend = 50
                    currentYLeftLegend += legendLineHeight
                }
                ;(
                    theGroup as {
                        attr: (p: {
                            translateX: number
                            translateY: number
                        }) => void
                    }
                ).attr({
                    translateX: currentXLeftLegend,
                    translateY: currentYLeftLegend,
                })
                currentXLeftLegend =
                    currentXLeftLegend +
                    item.itemWidth -
                    (legendOptions.considerSymbols ? 10 : 0)
            }
        })
    }

    H.wrap(H.Legend.prototype, 'render', wrapLegend)
}

wrapLegendRendering(Highcharts)
